import { useQuery } from "@tanstack/react-query";
import { AlertCircle, Check, Clock, Copy, Download, DollarSign, TrendingUp } from "lucide-react";
import { useMemo, useState, useCallback } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  type PieLabelRenderProps,
} from "recharts";

import { apiClient } from "@/lib/api";
import { usePageTitle } from "@/lib/use-page-title";
import type { AiCostAuditParams, AwsCostsParams } from "@tilt/admin-sdk";

// ---------------------------------------------------------------- //
// Constants
// ---------------------------------------------------------------- //

const COLORS = [
  "#6366f1",
  "#f59e0b",
  "#10b981",
  "#ef4444",
  "#8b5cf6",
  "#14b8a6",
  "#f97316",
  "#06b6d4",
];

type TopTab = "overview" | "aws" | "ai";
type AiSubTab = "summary" | "audit";
type Preset = "mtd" | "7d" | "30d" | "3m" | "12m" | "custom";

const PRESET_LABEL: Record<Preset, string> = {
  mtd: "MTD",
  "7d": "Last 7d",
  "30d": "Last 30d",
  "3m": "Last 3m",
  "12m": "Last 12m",
  custom: "Custom",
};

// ---------------------------------------------------------------- //
// Date helpers
// ---------------------------------------------------------------- //

function monthStart(): string {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
}

function daysAgo(n: number): string {
  return new Date(Date.now() - n * 86400_000).toISOString();
}

function monthsAgo(n: number): string {
  const d = new Date();
  d.setMonth(d.getMonth() - n);
  return d.toISOString();
}

// ---------------------------------------------------------------- //
// Formatters
// ---------------------------------------------------------------- //

function fmtUsd(v: string | number | null | undefined, decimals = 2): string {
  if (v == null || v === "") return "-";
  const n = typeof v === "string" ? parseFloat(v) : v;
  return isNaN(n) ? "-" : `$${n.toFixed(decimals)}`;
}

function fmtNum(v: number | null | undefined): string {
  if (v == null) return "-";
  return v.toLocaleString();
}

function fmtMs(v: number | null | undefined): string {
  if (v == null) return "-";
  return v >= 1000 ? `${(v / 1000).toFixed(1)}s` : `${v}ms`;
}

function shortDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

// ---------------------------------------------------------------- //
// Shared time-range picker
// ---------------------------------------------------------------- //

interface DateRange {
  since: string;
  until: string;
}

interface TimeRangePickerProps {
  preset: Preset;
  onPreset: (p: Preset) => void;
  customSince: string;
  customUntil: string;
  onCustomSince: (v: string) => void;
  onCustomUntil: (v: string) => void;
}

function TimeRangePicker({
  preset,
  onPreset,
  customSince,
  customUntil,
  onCustomSince,
  onCustomUntil,
}: TimeRangePickerProps): JSX.Element {
  return (
    <div className="rounded-lg border bg-card p-3 shadow-sm sm:p-4 space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Window
        </span>
        {(["mtd", "7d", "30d", "3m", "12m", "custom"] as Preset[]).map((p) => (
          <button
            key={p}
            type="button"
            onClick={() => onPreset(p)}
            className={
              preset === p
                ? "rounded-md bg-primary px-2.5 py-1 text-xs font-medium text-primary-foreground"
                : "rounded-md border bg-background px-2.5 py-1 text-xs text-muted-foreground hover:bg-muted hover:text-foreground"
            }
          >
            {PRESET_LABEL[p]}
          </button>
        ))}
      </div>

      {preset === "custom" && (
        <div className="flex flex-wrap items-center gap-3">
          <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
            From
            <input
              type="datetime-local"
              value={customSince}
              onChange={(e) => onCustomSince(e.target.value)}
              className="rounded-md border bg-background px-2 py-1 text-xs"
            />
          </label>
          <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
            To
            <input
              type="datetime-local"
              value={customUntil}
              onChange={(e) => onCustomUntil(e.target.value)}
              className="rounded-md border bg-background px-2 py-1 text-xs"
            />
          </label>
        </div>
      )}
    </div>
  );
}

function useDateRange(preset: Preset, customSince: string, customUntil: string): DateRange {
  return useMemo(() => {
    if (preset === "custom") {
      return {
        since: customSince ? new Date(customSince).toISOString() : daysAgo(30),
        until: customUntil ? new Date(customUntil).toISOString() : new Date().toISOString(),
      };
    }
    const until = new Date().toISOString();
    const since =
      preset === "mtd"
        ? monthStart()
        : preset === "7d"
          ? daysAgo(7)
          : preset === "30d"
            ? daysAgo(30)
            : preset === "3m"
              ? monthsAgo(3)
              : monthsAgo(12);
    return { since, until };
  }, [preset, customSince, customUntil]);
}

// ---------------------------------------------------------------- //
// Overview tab
// ---------------------------------------------------------------- //

function OverviewTab({
  since,
  until,
}: {
  readonly since: string;
  readonly until: string;
}): JSX.Element {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["costs", "summary", since, until],
    queryFn: () => apiClient.getCostsSummary({ since, until }),
    retry: false,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16 text-sm text-muted-foreground">
        Loading...
      </div>
    );
  }
  if (isError || !data) {
    return (
      <p className="py-8 text-center text-sm text-destructive">Failed to load cost summary.</p>
    );
  }

  const awsUsd = parseFloat(data.by_category.aws_infra);
  const aiUsd = parseFloat(data.by_category.ai_llm);
  const totalUsd = parseFloat(data.total_usd);
  const pieData = [
    { name: "AWS Infra", value: awsUsd },
    { name: "AI / LLM", value: aiUsd },
  ];

  const ageHours = data.snapshot_age_hours;
  let freshnessClass = "text-emerald-600 dark:text-emerald-400";
  if (ageHours != null && ageHours > 48) freshnessClass = "text-destructive";
  else if (ageHours != null && ageHours > 24) freshnessClass = "text-amber-600 dark:text-amber-400";

  return (
    <div className="space-y-6">
      {/* No-data banner */}
      {data.snapshot_fetched_at == null && (
        <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200">
          <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
          <span>
            No AWS cost data yet. AWS Cost and Usage Report (CUR) export must be configured before
            infrastructure spend appears here.
          </span>
        </div>
      )}

      {/* Snapshot freshness */}
      {data.snapshot_fetched_at != null && (
        <div className={`flex items-center gap-1.5 text-xs ${freshnessClass}`}>
          <Clock className="h-3.5 w-3.5" />
          {ageHours != null
            ? `Data last refreshed ${ageHours.toFixed(1)}h ago`
            : `Refreshed at ${new Date(data.snapshot_fetched_at).toLocaleString()}`}
        </div>
      )}

      {/* Stat cards */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <div className="rounded-lg border bg-card p-4 shadow-sm">
          <p className="text-xs text-muted-foreground">Total spend</p>
          <p className="mt-1 text-2xl font-bold tabular-nums text-foreground">{fmtUsd(totalUsd)}</p>
        </div>
        <div className="rounded-lg border bg-card p-4 shadow-sm">
          <p className="text-xs text-muted-foreground">AWS infrastructure</p>
          <p className="mt-1 text-2xl font-bold tabular-nums text-foreground">{fmtUsd(awsUsd)}</p>
        </div>
        <div className="rounded-lg border bg-card p-4 shadow-sm">
          <p className="text-xs text-muted-foreground">AI / LLM</p>
          <p className="mt-1 text-2xl font-bold tabular-nums text-foreground">{fmtUsd(aiUsd, 4)}</p>
        </div>
        <div className="rounded-lg border bg-card p-4 shadow-sm">
          <p className="text-xs text-muted-foreground">AI calls</p>
          <p className="mt-1 text-2xl font-bold tabular-nums text-foreground">
            {fmtNum(data.ai_call_count)}
          </p>
        </div>
      </div>

      {/* Pie chart + partners table side by side */}
      <div className="grid gap-4 lg:grid-cols-2">
        {/* Category donut */}
        <div className="rounded-lg border bg-card p-4 shadow-sm">
          <p className="mb-2 text-sm font-medium text-foreground">Spend by category</p>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie
                data={pieData}
                cx="50%"
                cy="50%"
                innerRadius={55}
                outerRadius={85}
                paddingAngle={3}
                dataKey="value"
                label={({ name, percent }: PieLabelRenderProps) =>
                  `${String(name ?? "")} ${(Number(percent ?? 0) * 100).toFixed(0)}%`
                }
              >
                {pieData.map((_, i) => (
                  <Cell key={i} fill={COLORS[i % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip formatter={(v) => fmtUsd(Number(v))} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Top partners */}
        <div className="rounded-lg border bg-card shadow-sm overflow-hidden">
          <div className="border-b bg-muted/30 px-4 py-2.5">
            <p className="text-sm font-medium text-foreground">Top partners</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/40 text-left text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  <th className="px-4 py-2">Partner</th>
                  <th className="px-4 py-2 text-right">AWS (prorated)</th>
                  <th className="px-4 py-2 text-right">AI</th>
                  <th className="px-4 py-2 text-right">Total</th>
                  <th className="px-4 py-2 text-right">Txn share</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {data.by_partner.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="py-8 text-center text-sm text-muted-foreground">
                      No partner data in this window.
                    </td>
                  </tr>
                ) : (
                  data.by_partner.map((p) => (
                    <tr key={p.partner_id} className="hover:bg-muted/20">
                      <td className="px-4 py-2">
                        <p className="font-medium text-foreground">
                          {p.partner_name ?? p.partner_id}
                        </p>
                        {p.partner_path && (
                          <p className="text-xs text-muted-foreground font-mono">
                            {p.partner_path}
                          </p>
                        )}
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums">
                        {fmtUsd(p.aws_infra_usd)}
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums">
                        {fmtUsd(p.ai_llm_usd, 4)}
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums font-semibold">
                        {fmtUsd(p.total_usd)}
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums text-muted-foreground">
                        {p.infra_pct > 0
                          ? `${p.infra_pct.toFixed(1)}% of ${fmtNum(p.payment_count)} payments`
                          : fmtNum(p.ai_call_count) + " calls"}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- //
// AWS tab
// ---------------------------------------------------------------- //

type GroupBy = "service" | "partner" | "service_x_partner";
type Granularity = "DAILY" | "MONTHLY";

function AwsTab({ since, until }: { readonly since: string; readonly until: string }): JSX.Element {
  const [granularity, setGranularity] = useState<Granularity>("DAILY");
  const [groupBy, setGroupBy] = useState<GroupBy>("service");
  const [tagPage, setTagPage] = useState(0);
  const TAG_PAGE_SIZE = 20;

  const costsParams = useMemo<AwsCostsParams>(
    () => ({ since, until, granularity, group_by: groupBy }),
    [since, until, granularity, groupBy],
  );

  const costsQuery = useQuery({
    queryKey: ["costs", "aws", costsParams],
    queryFn: () => apiClient.getAwsCosts(costsParams),
    retry: false,
  });

  const forecastQuery = useQuery({
    queryKey: ["costs", "aws-forecast"],
    queryFn: () => apiClient.getAwsForecast(),
    retry: false,
    staleTime: 15 * 60_000,
  });

  const tagQuery = useQuery({
    queryKey: ["costs", "tag-coverage", tagPage],
    queryFn: () =>
      apiClient.getTagCoverage({ limit: TAG_PAGE_SIZE, offset: tagPage * TAG_PAGE_SIZE }),
    retry: false,
  });

  // Pivot flat data into chart-friendly format
  const { chartData, dimensions } = useMemo(() => {
    if (!costsQuery.data?.data.length) return { chartData: [], dimensions: [] as string[] };

    const byPeriod = new Map<string, Record<string, number>>();
    const dimSet = new Set<string>();

    for (const row of costsQuery.data.data) {
      const key = row.date ?? row.period_start;
      if (!byPeriod.has(key)) byPeriod.set(key, {});
      const entry = byPeriod.get(key)!;
      const dim = row.dimension || "Unknown";
      dimSet.add(dim);
      entry[dim] = (entry[dim] ?? 0) + parseFloat(row.amount_usd);
      entry.__period = key as unknown as number;
    }

    const dims = Array.from(dimSet).sort();
    const sorted = Array.from(byPeriod.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([period, vals]) => ({ period: shortDate(period), ...vals }));

    return { chartData: sorted, dimensions: dims };
  }, [costsQuery.data]);

  return (
    <div className="space-y-6">
      {/* Controls */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-1 rounded-md border bg-card p-1">
          {(["DAILY", "MONTHLY"] as Granularity[]).map((g) => (
            <button
              key={g}
              type="button"
              onClick={() => setGranularity(g)}
              className={
                granularity === g
                  ? "rounded px-2.5 py-1 text-xs font-medium bg-primary text-primary-foreground"
                  : "rounded px-2.5 py-1 text-xs text-muted-foreground hover:text-foreground"
              }
            >
              {g === "DAILY" ? "Daily" : "Monthly"}
            </button>
          ))}
        </div>

        <select
          value={groupBy}
          onChange={(e) => setGroupBy(e.target.value as GroupBy)}
          className="rounded-md border bg-card px-2.5 py-1.5 text-xs text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
        >
          <option value="service">By service</option>
          <option value="partner">By partner</option>
          <option value="service_x_partner">Service x partner</option>
        </select>
      </div>

      {/* Forecast card */}
      {forecastQuery.data && (
        <div className="rounded-lg border bg-card p-4 shadow-sm">
          <div className="flex items-center gap-2 mb-3">
            <TrendingUp className="h-4 w-4 text-indigo-500" />
            <p className="text-sm font-medium text-foreground">Forecast</p>
            {forecastQuery.data.stale && (
              <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
                stale
              </span>
            )}
          </div>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-2">
            <div>
              <p className="text-xs text-muted-foreground">This month projected</p>
              <p
                className={`mt-1 text-xl font-bold tabular-nums ${
                  forecastQuery.data.stale ? "text-muted-foreground" : "text-foreground"
                }`}
              >
                {fmtUsd(forecastQuery.data.current_month_projected_usd)}
              </p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Next month projected</p>
              <p
                className={`mt-1 text-xl font-bold tabular-nums ${
                  forecastQuery.data.stale ? "text-muted-foreground" : "text-foreground"
                }`}
              >
                {fmtUsd(forecastQuery.data.next_month_projected_usd)}
              </p>
            </div>
          </div>
          {forecastQuery.data.last_fetched_at && (
            <p className="mt-2 text-[11px] text-muted-foreground">
              Fetched {new Date(forecastQuery.data.last_fetched_at).toLocaleString()}
            </p>
          )}
        </div>
      )}

      {/* Spend chart */}
      <div className="rounded-lg border bg-card p-4 shadow-sm">
        <p className="mb-3 text-sm font-medium text-foreground">
          AWS spend over time (
          {groupBy === "service"
            ? "by service"
            : groupBy === "partner"
              ? "by partner"
              : "service x partner"}
          )
        </p>

        {costsQuery.isLoading && (
          <div className="flex items-center justify-center py-16 text-sm text-muted-foreground">
            Loading...
          </div>
        )}
        {costsQuery.isError && (
          <p className="py-8 text-center text-sm text-destructive">Failed to load AWS cost data.</p>
        )}
        {!costsQuery.isLoading && !costsQuery.isError && chartData.length === 0 && (
          <p className="py-8 text-center text-sm text-muted-foreground">
            No AWS cost data for this window. CUR setup may be required.
          </p>
        )}
        {chartData.length > 0 && (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData} margin={{ top: 4, right: 16, bottom: 4, left: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="currentColor" opacity={0.08} />
              <XAxis dataKey="period" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
              <YAxis
                tick={{ fontSize: 11 }}
                tickLine={false}
                axisLine={false}
                tickFormatter={(v: number) => `$${v.toFixed(0)}`}
              />
              <Tooltip
                formatter={(v, name) => [fmtUsd(Number(v)), String(name)]}
                contentStyle={{ fontSize: 12 }}
              />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              {dimensions.map((dim, i) => (
                <Bar
                  key={dim}
                  dataKey={dim}
                  stackId="a"
                  fill={COLORS[i % COLORS.length]}
                  name={dim}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        )}
        {costsQuery.data && (
          <p className="mt-1 text-[11px] text-muted-foreground">
            {costsQuery.data.snapshot_count} snapshot
            {costsQuery.data.snapshot_count !== 1 ? "s" : ""} loaded
            {costsQuery.data.fetched_at
              ? ` - fetched ${new Date(costsQuery.data.fetched_at).toLocaleString()}`
              : ""}
          </p>
        )}
      </div>

      {/* Tag coverage */}
      <div className="rounded-lg border bg-card shadow-sm">
        <div className="border-b bg-muted/30 px-4 py-3 flex items-center justify-between">
          <p className="text-sm font-medium text-foreground">Tag coverage</p>
          {tagQuery.data && (
            <span className="text-xs text-muted-foreground">
              {tagQuery.data.tagged_count} / {tagQuery.data.total_resources} resources tagged
            </span>
          )}
        </div>

        {tagQuery.isLoading && (
          <div className="flex items-center justify-center py-8 text-sm text-muted-foreground">
            Loading...
          </div>
        )}
        {tagQuery.isError && (
          <p className="py-4 text-center text-sm text-destructive">Failed to load tag coverage.</p>
        )}

        {tagQuery.data && (
          <div className="p-4 space-y-4">
            {/* Progress bar */}
            <div>
              <div className="flex items-center justify-between mb-1 text-xs">
                <span className="text-muted-foreground">Tagged resources</span>
                <span
                  className={
                    tagQuery.data.coverage_pct >= 80
                      ? "font-semibold text-emerald-600"
                      : tagQuery.data.coverage_pct >= 50
                        ? "font-semibold text-amber-600"
                        : "font-semibold text-destructive"
                  }
                >
                  {tagQuery.data.coverage_pct.toFixed(1)}%
                </span>
              </div>
              <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${
                    tagQuery.data.coverage_pct >= 80
                      ? "bg-emerald-500"
                      : tagQuery.data.coverage_pct >= 50
                        ? "bg-amber-500"
                        : "bg-destructive"
                  }`}
                  style={{ width: `${Math.min(tagQuery.data.coverage_pct, 100)}%` }}
                />
              </div>
              <p className="mt-1 text-xs text-muted-foreground">
                {tagQuery.data.untagged_count} untagged resource
                {tagQuery.data.untagged_count !== 1 ? "s" : ""}
              </p>
            </div>

            {/* Untagged resources table */}
            {tagQuery.data.untagged.length > 0 && (
              <>
                <div className="overflow-x-auto rounded-md border">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-muted/40 text-left text-xs font-medium uppercase tracking-wide text-muted-foreground">
                        <th className="px-3 py-2">ARN</th>
                        <th className="px-3 py-2">Type</th>
                        <th className="px-3 py-2">Partner tag</th>
                        <th className="px-3 py-2">Env tag</th>
                        <th className="px-3 py-2">Service tag</th>
                        <th className="px-3 py-2">Last scanned</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {tagQuery.data.untagged.map((r) => (
                        <tr key={r.resource_arn} className="hover:bg-muted/20">
                          <td className="px-3 py-2 font-mono text-xs text-muted-foreground max-w-sm">
                            <div className="flex items-center gap-1.5 min-w-0">
                              <span className="truncate" title={r.resource_arn}>
                                {r.resource_arn}
                              </span>
                              <CopyArnButton arn={r.resource_arn} />
                            </div>
                          </td>
                          <td className="px-3 py-2 text-xs">{r.resource_type}</td>
                          <td className="px-3 py-2 text-xs">
                            {r.partner_tag ?? <span className="text-destructive">missing</span>}
                          </td>
                          <td className="px-3 py-2 text-xs">
                            {r.environment_tag ?? <span className="text-destructive">missing</span>}
                          </td>
                          <td className="px-3 py-2 text-xs">
                            {r.service_tag ?? <span className="text-destructive">missing</span>}
                          </td>
                          <td className="px-3 py-2 text-xs text-muted-foreground whitespace-nowrap">
                            {r.last_scanned_at
                              ? new Date(r.last_scanned_at).toLocaleDateString()
                              : "-"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Tag coverage pagination */}
                {(tagQuery.data.has_more || tagPage > 0) && (
                  <div className="flex items-center justify-between text-sm">
                    <button
                      type="button"
                      onClick={() => setTagPage(0)}
                      disabled={tagPage === 0}
                      className="rounded-md border bg-card px-3 py-1.5 hover:bg-muted disabled:opacity-40"
                    >
                      First page
                    </button>
                    <span className="text-xs text-muted-foreground">Page {tagPage + 1}</span>
                    <button
                      type="button"
                      onClick={() => setTagPage((p) => p + 1)}
                      disabled={!tagQuery.data.has_more}
                      className="rounded-md border bg-card px-3 py-1.5 hover:bg-muted disabled:opacity-40"
                    >
                      Next page
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- //
// AI summary sub-tab
// ---------------------------------------------------------------- //

// Human-readable label for each recipe_kind value
const RECIPE_LABELS: Record<string, string> = {
  chat_turn: "Chat",
  cashier_dashboard: "Dashboard",
  cashier_location_summary: "Location summary",
  cashier_pos_nudge: "POS nudge",
  cashier_exception_triage: "Exception triage",
};

function recipeLabel(kind: string): string {
  return RECIPE_LABELS[kind] ?? kind;
}

function AiSummaryTab({
  since,
  until,
}: {
  readonly since: string;
  readonly until: string;
}): JSX.Element {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["costs", "ai-summary", since, until],
    queryFn: () => apiClient.getCostsAiSummary({ since, until }),
    retry: false,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16 text-sm text-muted-foreground">
        Loading...
      </div>
    );
  }
  if (isError || !data) {
    return <p className="py-8 text-center text-sm text-destructive">Failed to load AI summary.</p>;
  }

  const totalUsd = parseFloat(data.total_cost_usd);

  // Partner spend for bar chart
  const partnerSpendForChart = data.partners.map((p) => ({
    name: p.partner_name ?? p.partner_id.slice(0, 8),
    cost: parseFloat(p.cost_usd),
    calls: p.call_count,
  }));

  // Aggregate recipe spend across all partners
  const recipeMap = new Map<string, { cost: number; calls: number }>();
  for (const p of data.partners) {
    for (const rb of p.recipe_breakdown ?? []) {
      const existing = recipeMap.get(rb.recipe_kind) ?? { cost: 0, calls: 0 };
      recipeMap.set(rb.recipe_kind, {
        cost: existing.cost + parseFloat(rb.cost_usd),
        calls: existing.calls + rb.call_count,
      });
    }
  }
  const recipeChartData = Array.from(recipeMap.entries())
    .map(([kind, { cost, calls }]) => ({ name: recipeLabel(kind), cost, calls, kind }))
    .sort((a, b) => b.cost - a.cost);

  // Aggregate caller (source) spend across all partners
  const callerMap = new Map<string, { cost: number; calls: number }>();
  for (const p of data.partners) {
    for (const cb of p.caller_breakdown ?? []) {
      const existing = callerMap.get(cb.caller_kind) ?? { cost: 0, calls: 0 };
      callerMap.set(cb.caller_kind, {
        cost: existing.cost + parseFloat(cb.cost_usd),
        calls: existing.calls + cb.call_count,
      });
    }
  }
  const callerChartData = Array.from(callerMap.entries())
    .map(([kind, { cost, calls }]) => ({
      name: kind === "pos_user" ? "POS app" : kind === "m2m" ? "M2M API" : kind,
      cost,
      calls,
      kind,
    }))
    .sort((a, b) => b.cost - a.cost);

  return (
    <div className="space-y-6">
      {/* Headline totals */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
        <div className="rounded-lg border bg-card p-4 shadow-sm">
          <p className="text-xs text-muted-foreground">Total AI spend</p>
          <p className="mt-1 text-2xl font-bold tabular-nums text-foreground">
            ${totalUsd.toFixed(4)}
          </p>
        </div>
        <div className="rounded-lg border bg-card p-4 shadow-sm">
          <p className="text-xs text-muted-foreground">Total LLM calls</p>
          <p className="mt-1 text-2xl font-bold tabular-nums text-foreground">
            {fmtNum(data.total_calls)}
          </p>
        </div>
        <div className="rounded-lg border bg-card p-4 shadow-sm">
          <p className="text-xs text-muted-foreground">Partners using AI</p>
          <p className="mt-1 text-2xl font-bold tabular-nums text-foreground">
            {data.partners.length}
          </p>
        </div>
      </div>

      {/* Charts: by partner, by recipe type, by source (POS vs M2M) */}
      {(partnerSpendForChart.length > 0 ||
        recipeChartData.length > 0 ||
        callerChartData.length > 0) && (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          {partnerSpendForChart.length > 0 && (
            <div className="rounded-lg border bg-card p-4 shadow-sm">
              <p className="mb-3 text-sm font-medium text-foreground">Spend by partner</p>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart
                  data={partnerSpendForChart}
                  margin={{ top: 4, right: 16, bottom: 4, left: 8 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="currentColor" opacity={0.08} />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
                  <YAxis
                    tick={{ fontSize: 11 }}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(v: number) => `$${v.toFixed(2)}`}
                  />
                  <Tooltip
                    formatter={(v) => [`$${Number(v).toFixed(4)}`, "Cost"]}
                    contentStyle={{ fontSize: 12 }}
                  />
                  <Bar dataKey="cost" fill={COLORS[0]} name="AI spend" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {recipeChartData.length > 0 && (
            <div className="rounded-lg border bg-card p-4 shadow-sm">
              <p className="mb-3 text-sm font-medium text-foreground">Spend by recipe type</p>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={recipeChartData} margin={{ top: 4, right: 16, bottom: 4, left: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="currentColor" opacity={0.08} />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
                  <YAxis
                    tick={{ fontSize: 11 }}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(v: number) => `$${v.toFixed(2)}`}
                  />
                  <Tooltip
                    formatter={(v, _name, entry) => [
                      `$${Number(v).toFixed(4)} (${fmtNum((entry.payload as { calls?: number })?.calls)} calls)`,
                      "Cost",
                    ]}
                    contentStyle={{ fontSize: 12 }}
                  />
                  <Bar dataKey="cost" name="AI spend" radius={[3, 3, 0, 0]}>
                    {recipeChartData.map((entry, idx) => (
                      <Cell key={entry.kind} fill={COLORS[idx % COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {callerChartData.length > 0 && (
            <div className="rounded-lg border bg-card p-4 shadow-sm">
              <p className="mb-3 text-sm font-medium text-foreground">Spend by source</p>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={callerChartData} margin={{ top: 4, right: 16, bottom: 4, left: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="currentColor" opacity={0.08} />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
                  <YAxis
                    tick={{ fontSize: 11 }}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(v: number) => `$${v.toFixed(2)}`}
                  />
                  <Tooltip
                    formatter={(v, _name, entry) => [
                      `$${Number(v).toFixed(4)} (${fmtNum((entry.payload as { calls?: number })?.calls)} calls)`,
                      "Cost",
                    ]}
                    contentStyle={{ fontSize: 12 }}
                  />
                  <Bar dataKey="cost" name="AI spend" radius={[3, 3, 0, 0]}>
                    {callerChartData.map((entry, idx) => (
                      <Cell key={entry.kind} fill={COLORS[(idx + 3) % COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      )}

      {/* Per-partner table */}
      {data.partners.length === 0 ? (
        <p className="text-center text-sm text-muted-foreground py-8">
          No AI calls in this window.
        </p>
      ) : (
        <div className="rounded-lg border bg-card shadow-sm overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/40 text-left text-xs font-medium uppercase tracking-wide text-muted-foreground">
                <th className="px-4 py-2.5">Partner</th>
                <th className="px-4 py-2.5 text-right">Calls</th>
                <th className="px-4 py-2.5 text-right">Prompt tokens</th>
                <th className="px-4 py-2.5 text-right">Completion tokens</th>
                <th className="px-4 py-2.5 text-right">Cost (USD)</th>
                <th className="px-4 py-2.5">Source</th>
                <th className="px-4 py-2.5">Recipe types</th>
                <th className="px-4 py-2.5">Models</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {data.partners.map((p) => (
                <tr key={p.partner_id} className="hover:bg-muted/20">
                  <td className="px-4 py-2.5">
                    <p className="font-medium text-foreground">{p.partner_name ?? p.partner_id}</p>
                    {p.partner_path && (
                      <p className="text-xs text-muted-foreground font-mono">{p.partner_path}</p>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-right tabular-nums">{fmtNum(p.call_count)}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums">{fmtNum(p.prompt_tokens)}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums">
                    {fmtNum(p.completion_tokens)}
                  </td>
                  <td className="px-4 py-2.5 text-right tabular-nums font-semibold">
                    {fmtUsd(p.cost_usd, 4)}
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="flex flex-wrap gap-1">
                      {(p.caller_breakdown ?? []).map((cb) => (
                        <span
                          key={cb.caller_kind}
                          className={`rounded-full px-1.5 py-0.5 text-[10px] font-medium ${
                            cb.caller_kind === "m2m"
                              ? "bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300"
                              : "bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300"
                          }`}
                          title={`${cb.call_count} calls - ${fmtUsd(cb.cost_usd, 4)}`}
                        >
                          {cb.caller_kind === "pos_user"
                            ? "POS"
                            : cb.caller_kind === "m2m"
                              ? "M2M"
                              : cb.caller_kind}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="flex flex-wrap gap-1">
                      {(p.recipe_breakdown ?? []).map((rb, idx) => (
                        <span
                          key={rb.recipe_kind}
                          className="rounded-full px-1.5 py-0.5 text-[10px] font-medium"
                          style={{
                            backgroundColor: `${COLORS[idx % COLORS.length]}22`,
                            color: COLORS[idx % COLORS.length],
                          }}
                          title={`${rb.call_count} calls - ${fmtUsd(rb.cost_usd, 4)}`}
                        >
                          {recipeLabel(rb.recipe_kind)}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="flex flex-wrap gap-1">
                      {p.model_breakdown.map((mb) => (
                        <span
                          key={mb.model}
                          className="rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-mono text-muted-foreground"
                          title={`${mb.call_count} calls - ${fmtUsd(mb.cost_usd, 4)}`}
                        >
                          {mb.model}
                        </span>
                      ))}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- //
// AI audit sub-tab
// ---------------------------------------------------------------- //

function AiAuditTab({
  since,
  until,
}: {
  readonly since: string;
  readonly until: string;
}): JSX.Element {
  const [cursor, setCursor] = useState<string | undefined>(undefined);

  const params = useMemo<AiCostAuditParams>(() => {
    const p: AiCostAuditParams = { limit: 50, since, until };
    if (cursor) p.cursor = cursor;
    return p;
  }, [since, until, cursor]);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["costs", "ai-audit", params],
    queryFn: () => apiClient.listCostsAiAudit(params),
    retry: false,
  });

  const exportCsv = (): void => {
    const items = data?.items ?? [];
    if (items.length === 0) return;
    const headers = [
      "created_at",
      "partner",
      "user",
      "recipe_kind",
      "source",
      "provider",
      "model",
      "prompt_tokens",
      "completion_tokens",
      "cost_usd",
      "http_status",
      "duration_ms",
      "error_kind",
    ];
    const rows = items.map((r) => [
      r.created_at,
      r.partner_name ?? r.partner_id,
      r.user_display_name ?? "",
      r.recipe_kind,
      r.caller_kind,
      r.provider_slug,
      r.model,
      r.prompt_tokens ?? "",
      r.completion_tokens ?? "",
      r.cost_usd ?? "",
      r.http_status ?? "",
      r.duration_ms ?? "",
      r.error_kind ?? "",
    ]);
    const csv = [headers, ...rows]
      .map((row) => row.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const stamp = new Date().toISOString().slice(0, 19).replace(/:/g, "-");
    a.download = `ai-costs-${stamp}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <button
          type="button"
          onClick={exportCsv}
          disabled={(data?.items.length ?? 0) === 0}
          className="inline-flex items-center gap-2 rounded-md border bg-card px-3 py-1.5 text-sm hover:bg-muted disabled:opacity-40"
        >
          <Download className="h-4 w-4" />
          Export CSV
        </button>
      </div>

      {isLoading && (
        <div className="flex items-center justify-center py-16 text-sm text-muted-foreground">
          Loading...
        </div>
      )}
      {isError && (
        <p className="py-8 text-center text-sm text-destructive">Failed to load audit rows.</p>
      )}

      {!isLoading && !isError && (
        <>
          <div className="rounded-lg border bg-card shadow-sm overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/40 text-left text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  <th className="px-4 py-2.5">Time</th>
                  <th className="px-4 py-2.5">Partner</th>
                  <th className="px-4 py-2.5">User</th>
                  <th className="px-4 py-2.5">Recipe</th>
                  <th className="px-4 py-2.5">Source</th>
                  <th className="px-4 py-2.5">Model</th>
                  <th className="px-4 py-2.5 text-right">Tokens (P/C)</th>
                  <th className="px-4 py-2.5 text-right">Cost</th>
                  <th className="px-4 py-2.5 text-right">Latency</th>
                  <th className="px-4 py-2.5">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {(data?.items ?? []).map((r) => (
                  <tr key={r.id} className="hover:bg-muted/20">
                    <td className="px-4 py-2 text-xs text-muted-foreground tabular-nums whitespace-nowrap">
                      {new Date(r.created_at).toLocaleString()}
                    </td>
                    <td className="px-4 py-2 text-xs font-medium">
                      {r.partner_name ?? r.partner_id.slice(0, 8)}
                    </td>
                    <td className="px-4 py-2 text-xs text-muted-foreground">
                      {r.user_display_name ?? "-"}
                    </td>
                    <td className="px-4 py-2 font-mono text-xs">{r.recipe_kind}</td>
                    <td className="px-4 py-2 text-xs">
                      <span
                        className={`rounded-full px-1.5 py-0.5 text-[10px] font-medium ${
                          r.caller_kind === "m2m"
                            ? "bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300"
                            : "bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300"
                        }`}
                      >
                        {r.caller_kind === "pos_user"
                          ? "POS"
                          : r.caller_kind === "m2m"
                            ? "M2M"
                            : r.caller_kind}
                      </span>
                    </td>
                    <td className="px-4 py-2 font-mono text-xs">{r.model}</td>
                    <td className="px-4 py-2 text-right tabular-nums text-xs">
                      {fmtNum(r.prompt_tokens)} / {fmtNum(r.completion_tokens)}
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums text-xs font-semibold">
                      {fmtUsd(r.cost_usd, 4)}
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums text-xs">
                      {fmtMs(r.duration_ms)}
                    </td>
                    <td className="px-4 py-2 text-xs">
                      {r.error_kind ? (
                        <span className="rounded-full bg-destructive/10 px-1.5 py-0.5 text-destructive text-[10px] font-medium">
                          {r.error_kind}
                        </span>
                      ) : (
                        <span className="rounded-full bg-emerald-100 px-1.5 py-0.5 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300 text-[10px] font-medium">
                          {r.http_status ?? "ok"}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
                {(data?.items ?? []).length === 0 && (
                  <tr>
                    <td colSpan={10} className="py-10 text-center text-sm text-muted-foreground">
                      No rows in this window.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {(data?.next_cursor || cursor) && (
            <div className="flex items-center justify-between text-sm">
              <button
                type="button"
                onClick={() => setCursor(undefined)}
                disabled={!cursor}
                className="rounded-md border bg-card px-3 py-1.5 hover:bg-muted disabled:opacity-40"
              >
                First page
              </button>
              <button
                type="button"
                onClick={() => {
                  if (data?.next_cursor) setCursor(data.next_cursor);
                }}
                disabled={!data?.next_cursor}
                className="rounded-md border bg-card px-3 py-1.5 hover:bg-muted disabled:opacity-40"
              >
                Next page
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- //
// AI tab (wraps summary + audit sub-tabs)
// ---------------------------------------------------------------- //

function AiTab({ since, until }: { readonly since: string; readonly until: string }): JSX.Element {
  const [subTab, setSubTab] = useState<AiSubTab>("summary");

  return (
    <div className="space-y-4">
      <div className="flex gap-1 border-b">
        {(["summary", "audit"] as AiSubTab[]).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setSubTab(t)}
            className={
              subTab === t
                ? "border-b-2 border-primary px-4 py-2 text-sm font-medium text-foreground"
                : "px-4 py-2 text-sm text-muted-foreground hover:text-foreground"
            }
          >
            {t === "summary" ? "Summary" : "Raw audit"}
          </button>
        ))}
      </div>

      {subTab === "summary" ? (
        <AiSummaryTab since={since} until={until} />
      ) : (
        <AiAuditTab since={since} until={until} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------- //
function CopyArnButton({ arn }: { arn: string }): JSX.Element {
  const [copied, setCopied] = useState(false);
  const copy = useCallback(() => {
    void navigator.clipboard.writeText(arn).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }, [arn]);
  return (
    <button
      onClick={copy}
      title="Copy full ARN"
      className="shrink-0 text-muted-foreground hover:text-foreground transition-colors"
    >
      {copied ? <Check className="h-3 w-3 text-green-500" /> : <Copy className="h-3 w-3" />}
    </button>
  );
}

// Main page
// ---------------------------------------------------------------- //

export function CostsPage(): JSX.Element {
  usePageTitle("Costs");
  const [topTab, setTopTab] = useState<TopTab>("overview");
  const [preset, setPreset] = useState<Preset>("mtd");
  const [customSince, setCustomSince] = useState("");
  const [customUntil, setCustomUntil] = useState("");

  const { since, until } = useDateRange(preset, customSince, customUntil);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-2">
        <DollarSign className="h-5 w-5 text-indigo-500 shrink-0" aria-hidden="true" />
        <div>
          <h1 className="text-xl font-semibold tracking-tight sm:text-2xl">Costs</h1>
          <p className="text-sm text-muted-foreground">
            AWS infrastructure and AI/LLM spend across the platform.
          </p>
        </div>
      </div>

      {/* Time range picker */}
      <TimeRangePicker
        preset={preset}
        onPreset={setPreset}
        customSince={customSince}
        customUntil={customUntil}
        onCustomSince={setCustomSince}
        onCustomUntil={setCustomUntil}
      />

      {/* Top-level tab bar */}
      <div className="flex gap-1 border-b">
        {(["overview", "aws", "ai"] as TopTab[]).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTopTab(t)}
            className={
              topTab === t
                ? "border-b-2 border-primary px-4 py-2 text-sm font-medium text-foreground"
                : "px-4 py-2 text-sm text-muted-foreground hover:text-foreground"
            }
          >
            {t === "overview" ? "Overview" : t === "aws" ? "AWS" : "AI / LLM"}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {topTab === "overview" && <OverviewTab since={since} until={until} />}
      {topTab === "aws" && <AwsTab since={since} until={until} />}
      {topTab === "ai" && <AiTab since={since} until={until} />}
    </div>
  );
}
